import React from 'react';
import styled from 'styled-components';
import type { CartItem, DynamicSpecItem } from '../../App';
import { getTranslatedValue, getSpecLabel, formatPrice, formatProductName, getLocaleForLanguage } from './utils';
import { getSpecIcon } from '../../utils/iconMapper';

// Props interface
interface CartItemCardProps {
  item: CartItem;
  quantity: number;
  onQuantityChange: (id: string, change: number) => void;
  onDelete: (id: string) => void;
  language: string;
  t: (key: string) => string;
}

// Styled Components
const CartCard = styled.div`
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 10px 28px rgba(16, 24, 40, 0.06);
  border: 1px solid #e6edf5;
  margin-bottom: 2rem;
  padding: 2rem;
  position: relative;
  overflow: hidden;

  .dark & {
    background: #0f1216;
    border: 1px solid #2e3441;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.6);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 20px;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.1rem 1rem;
    border-radius: 16px;
    margin-bottom: 1.25rem;
  }
`;

const ItemRemoveButton = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  color: #ef4444;
  padding: 0;
  cursor: pointer;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  will-change: opacity;
  z-index: 10;

  &:hover {
    background: #ef4444;
    color: white;
    border-color: #ef4444;
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 480px) {
    top: 1rem;
    right: 1rem;
    width: 28px;
    height: 28px;
    svg { width: 14px; height: 14px; }
  }
`;

const ProductHeader = styled.div`
  display: flex;
  gap: 1.25rem;
  padding-bottom: 1.25rem;
  margin-bottom: 0.5rem;
  position: relative;
  align-items: flex-start;

  @media (max-width: 768px) {
    gap: 1rem;
    padding-bottom: 1rem;
  }

  @media (max-width: 480px) {
    gap: 0.875rem;
    padding-bottom: 0.75rem;
  }
`;

const ImageSection = styled.div`
  width: 100px;
  height: 100px;
  background: #f8fafc;
  border-radius: 16px;
  border: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  flex-shrink: 0;

  .dark & {
    background: rgba(255, 255, 255, 0.04);
    border-color: #1e293b;
  }

  @media (max-width: 768px) {
    width: 90px;
    height: 90px;
    border-radius: 14px;
  }

  @media (max-width: 480px) {
    width: 76px;
    height: 76px;
    border-radius: 12px;
    padding: 0.4rem;
  }
`;

const DeviceImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: all 0.3s ease;
`;

const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.375rem;
`;

const DeviceName = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 0.5rem 0;
  line-height: 1.3;

  .dark & { color: #f1f5f9; }

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const ProductPrice = styled.div`
  flex: 1;
`;

const PriceTitle = styled.span`
  font-size: 0.875rem;
  color: #6e6e73;
  font-weight: 500;
  display: block;
  margin-bottom: 0.125rem;

  .dark & {
    color: #94a3b8;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const PriceAmount = styled.h1`
  font-size: 1.5rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  line-height: 1.15;
  letter-spacing: -0.02em;

  .dark & {
    color: #f1f5f9;
  }

  @media (max-width: 768px) {
    font-size: 1.35rem;
  }

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const QuantityControl = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 480px) {
    gap: 0.35rem;
  }
`;

const QuantityLabel = styled.span`
  font-size: 0.875rem;
  color: #6e6e73;
  margin-bottom: 0.5rem;
  font-weight: 500;
  display: block;

  .dark & {
    color: #94a3b8;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
    margin-bottom: 0.35rem;
  }
`;

const QuantityInputWrapper = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  width: fit-content;
  background-color: #f9fafb;

  .dark & {
    border-color: #334155;
    background-color: rgba(255, 255, 255, 0.06);
  }

  @media (max-width: 480px) {
    border-radius: 8px;
  }
`;

const QuantityButton = styled.button`
  background: transparent;
  border: none;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #007aff;
  font-size: 1.25rem;
  transition: background-color 0.2s ease;

  &:hover {
    background: #f0f0f0;
  }

  &:disabled {
    color: #d1d5db;
    cursor: not-allowed;
  }

  .dark & {
    color: #93c5fd;
  }
  .dark &:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    font-size: 1.1rem;
  }

  @media (max-width: 360px) {
    width: 30px;
    height: 30px;
    font-size: 1rem;
  }
`;

const QuantityValue = styled.span`
  padding: 0 0.75rem;
  font-weight: 600;
  color: #1d1d1f;
  font-size: 1rem;
  min-width: 20px;
  text-align: center;

  .dark & {
    color: #e5e7eb;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    padding: 0 0.65rem;
    min-width: 18px;
  }
`;

const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  margin-bottom: 1.5rem;

  .info-icon {
    width: 32px;
    height: 32px;
    border-radius: 10px;
    background: #0f172a;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
      width: 16px;
      height: 16px;
      color: white;
    }
  }

  span {
    font-size: 0.85rem;
    font-weight: 500;
    color: #475569;
  }

  .dark & {
    background: #1a1f2e;
    border-color: #2d3548;

    .info-icon {
      background: #e2e8f0;
      svg { color: #0f172a; }
    }
    span { color: #94a3b8; }
  }

  @media (max-width: 480px) {
    padding: 0.625rem 0.875rem;
    gap: 0.625rem;
    .info-icon { width: 28px; height: 28px; border-radius: 8px; svg { width: 14px; height: 14px; } }
    span { font-size: 0.8rem; }
  }
`;

const CartTotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  margin-top: 1rem;

  .dark & {
    background: linear-gradient(135deg, #1a1f2e 0%, #151922 100%);
    border-color: #2e3441;
  }

  @media (max-width: 480px) {
    padding: 1rem 1.25rem;
    border-radius: 12px;
  }
`;

const CartTotalLabel = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  .dark & { color: #94a3b8; }
`;

const CartTotalAmount = styled.span`
  font-size: 1.75rem;
  font-weight: 800;
  color: #0f172a;
  display: block;
  letter-spacing: -0.02em;
  line-height: 1.1;

  .dark & { color: #f1f5f9; }

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const CartTotalVat = styled.span`
  font-size: 0.7rem;
  color: #64748b;
  font-weight: 500;
  display: block;
  margin-top: 0.2rem;

  .dark & { color: #94a3b8; }
`;

const OptionsSection = styled.div`
  padding: 0;

  @media (max-width: 480px) {
    padding: 0;
  }
`;

const OptionsTitle = styled.span`
  font-size: 0.95rem;
  display: block;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.75rem;

  .dark & { color: #e2e8f0; }

  @media (max-width: 480px) {
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DetailOptionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.875rem 0;
  border-bottom: 1px dashed #e5e7eb;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 0;
  }

  .dark & {
    border-bottom-color: #1e293b;
  }
`;

const OptionLabel = styled.span`
  font-size: 0.9rem;
  color: #1e293b;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;

  svg {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    color: #334155;
    padding: 6px;
    background: #f1f5f9;
    border-radius: 10px;
    box-sizing: border-box;
  }

  .dark & { color: #e5e7eb; }
  .dark & svg { color: #cbd5e1; background: #1a1f2e; }

  @media (max-width: 480px) {
    font-size: 0.85rem;
    gap: 0.625rem;
    svg { width: 28px; height: 28px; padding: 5px; border-radius: 8px; }
  }
`;

const OptionValue = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: #334155;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
  background: #f1f5f9;
  padding: 0.35rem 0.75rem;
  border-radius: 8px;

  .dark & { color: #cbd5e1; background: #1a1f2e; }

  @media (max-width: 480px) {
    font-size: 0.75rem;
    padding: 0.3rem 0.6rem;
  }
`;

// Icons (only keep TrashIcon which is unique to this component)
const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
  </svg>
);

// Note: getSpecIcon is imported from '../../utils/iconMapper'

// Main Component
const CartItemCard: React.FC<CartItemCardProps> = ({
  item,
  quantity,
  onQuantityChange,
  onDelete,
  language,
  t
}) => {
  return (
    <CartCard>
      <ItemRemoveButton onClick={() => onDelete(item.id)}>
        <TrashIcon />
      </ItemRemoveButton>

      <ProductHeader>
        <ImageSection>
          <DeviceImage src={encodeURI(item.image)} alt={formatProductName(item)} />
        </ImageSection>
        <ProductDetails>
          <DeviceName>{formatProductName(item)}</DeviceName>
          <ProductPrice>
            <PriceTitle>{t('estimatedPrice')}</PriceTitle>
            <PriceAmount>
              {formatPrice(item.price * quantity, t, getLocaleForLanguage(language))}
            </PriceAmount>
          </ProductPrice>
          <QuantityControl>
            <QuantityLabel>{t('quantity')}</QuantityLabel>
            <QuantityInputWrapper>
              <QuantityButton
                onClick={() => onQuantityChange(item.id, -1)}
                disabled={quantity <= 1}
              >
                -
              </QuantityButton>
              <QuantityValue>{quantity}</QuantityValue>
              <QuantityButton
                onClick={() => onQuantityChange(item.id, 1)}
              >
                +
              </QuantityButton>
            </QuantityInputWrapper>
          </QuantityControl>
        </ProductDetails>
      </ProductHeader>

      <InfoBanner>
        <div className="info-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
        </div>
        <span>{t('estimationBadge')}</span>
      </InfoBanner>

      <OptionsSection>
        <OptionsTitle>{t('chosenOptions')}</OptionsTitle>
        <OptionsList>
          {/* Dynamic specifications from Sales Questions (includes storage if configured) */}
          {item.specs.dynamicSpecs && item.specs.dynamicSpecs.length > 0 ? (
            item.specs.dynamicSpecs.map((spec) => {
              // Resolve label: JSONB multilingual → hardcoded map → stored snapshot
              const fromJsonbLabel = spec.question_labels?.[language] || spec.question_labels?.nl;
              const fromMapLabel = getSpecLabel(spec.questionKey, language);
              const questionLabel = fromJsonbLabel || (fromMapLabel !== spec.questionKey ? fromMapLabel : spec.question_label);

              // Resolve value: JSONB multilingual → hardcoded map → stored snapshot
              const fromJsonbValue = spec.option_labels?.[language] || spec.option_labels?.nl;
              const fromMapValue = getTranslatedValue(spec.questionKey, spec.optionKey, language);
              const optionValue = fromJsonbValue || (fromMapValue !== spec.optionKey ? fromMapValue : spec.option_label);

              return (
                <DetailOptionRow key={spec.questionKey}>
                  <OptionLabel>
                    {getSpecIcon(spec.questionKey)}
                    <span>{questionLabel}</span>
                  </OptionLabel>
                  <OptionValue>{optionValue}</OptionValue>
                </DetailOptionRow>
              );
            })
          ) : (
            /* Fallback: render legacy specs using Object.entries (for backward compatibility) */
            Object.entries(item.specs)
              .filter(([key, value]) =>
                !['price', 'imageSrc', 'image', 'deviceType', 'storage', 'dynamicSpecs'].includes(key) &&
                value !== undefined && value !== null && value !== ''
              )
              .map(([key, value]) => (
                <DetailOptionRow key={key}>
                  <OptionLabel>
                    {getSpecIcon(key)}
                    <span>{getSpecLabel(key, language)}</span>
                  </OptionLabel>
                  <OptionValue>{getTranslatedValue(key, value as string, language)}</OptionValue>
                </DetailOptionRow>
              ))
          )}
        </OptionsList>

        <CartTotalRow>
          <CartTotalLabel>{t('total') || 'Totaal'}</CartTotalLabel>
          <div style={{ textAlign: 'right' }}>
            <CartTotalAmount>
              {formatPrice(item.price * quantity, t, getLocaleForLanguage(language))}
            </CartTotalAmount>
          </div>
        </CartTotalRow>
      </OptionsSection>
    </CartCard>
  );
};

export default CartItemCard;
